import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { PromoBanner } from './models';
import {
  getPostPurchaseSurvey,
  getPromoBanners,
  getTask,
} from './home-data.reducer';
import { Observable } from 'rxjs';
import { List } from 'immutable';
import {
  IPostPurchaseSurvey,
  TaskItemStateEnum,
  TaskItemTypeEnum,
} from './home-data.data';
import { Task } from './models/task.model';
import { filter, map, withLatestFrom } from 'rxjs/operators';
import { TaskItem } from './models/submodels/task-item.model';

@Injectable()
export class HomeDataStore {
  constructor(private _store: Store<any>) {}

  getPromoBanners(): Observable<List<PromoBanner>> {
    return this._store.select(getPromoBanners);
  }

  getPostPurchaseSurvey(): Observable<IPostPurchaseSurvey> {
    return this._store.select(getPostPurchaseSurvey);
  }

  getTask(): Observable<Task> {
    return this._store.select(getTask).pipe(filter((task) => Boolean(task)));
  }

  /**
   * Active task item has incomplete or complete state
   */
  getActiveTaskItems(): Observable<List<TaskItem>> {
    return this.getTask().pipe(
      map((task: Task) => {
        const activeTaskItems = task.items
          .filter(
            (item: TaskItem) => item.state !== TaskItemStateEnum.dismissed
          )
          .sortBy((item: TaskItem) => item.position);

        return activeTaskItems as List<TaskItem>;
      })
    );
  }

  showTask(): Observable<boolean> {
    return this.getTask().pipe(
      withLatestFrom(this.getActiveTaskItems()),
      map(([task, activeTaskItems]: [Task, List<TaskItem>]) => {
        return !task.hasShownCongratulations && activeTaskItems.size > 0;
      })
    );
  }

  /**
   * Used to display tada dialog when no more incomplete state in active task item
   */
  isTaskReadyToComplete(): Observable<boolean> {
    return this.getTask().pipe(
      withLatestFrom(this.getActiveTaskItems()),
      map(([task, activeTaskItems]: [Task, List<TaskItem>]) => {
        const hasIncompleteStateInActiveTaskItems = activeTaskItems.some(
          (taskItem: TaskItem) =>
            taskItem.state === TaskItemStateEnum.incomplete
        );

        return (
          !task.hasShownCongratulations && !hasIncompleteStateInActiveTaskItems
        );
      })
    );
  }

  hasTaskItemWithIncompleteState(): Observable<boolean> {
    return this.getTask().pipe(
      map((task: Task) =>
        task.items.some(
          (taskItem: TaskItem) =>
            taskItem.state === TaskItemStateEnum.incomplete
        )
      )
    );
  }

  getTaskItemByTypeAndState(
    type: TaskItemTypeEnum,
    state: TaskItemStateEnum
  ): Observable<TaskItem | undefined> {
    return this.getTask().pipe(
      map((task: Task) => {
        return task.items.find(
          (taskItem: TaskItem) =>
            taskItem.type === type && taskItem.state === state
        );
      })
    );
  }
}
